import { FormEvent, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { DettaglioCassaforteRitiri, aggiornaImpostazioni, dettaglioCassaforteRitiri } from "../../api/ritiri";
import { PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { nomeGiorno } from "./presentazione";

/**
 * Impostazioni di una cassaforte (§ 7.5): categoria, ritiro fisso con
 * conferma, orario di esercizio, email di avviso al cliente, esclusione con
 * motivo. Si apre in `FinestraRitiri`, mai insieme a un'altra finestra.
 *
 * Il ritiro fisso lo imposta e lo conferma solo chi ha `ritiri:approva`: qui i
 * campi sono spenti per gli altri, e il backend lo rifiuta comunque.
 */

export const ID_TITOLO_IMPOSTAZIONI = "impostazioni-cassaforte-titolo";

const campo = "mt-1 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-ritiri-th-fondo";
const etichetta = "block text-xs font-bold text-ritiri-th-testo";

interface Props {
  idMacchina: string;
  /** Le categorie ammesse, nell'ordine del backend (le chiavi della stagionalità). */
  categorie: string[];
  onChiudi: () => void;
  onSalvate: () => void;
}

export function ImpostazioniCassaforte({ idMacchina, categorie, onChiudi, onSalvate }: Props) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const puoApprovare = hasPermission("ritiri:approva");
  const [d, setD] = useState<DettaglioCassaforteRitiri | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  // 409: un altro utente ha salvato dopo l'apertura del form. Si ricarica, non si sovrascrive.
  const [conflitto, setConflitto] = useState(false);
  const [caricamento, setCaricamento] = useState(0);

  const [categoria, setCategoria] = useState("");
  const [giorno, setGiorno] = useState("");
  const [confermato, setConfermato] = useState(false);
  const [orario, setOrario] = useState("");
  const [email, setEmail] = useState("");
  const [emailProposta, setEmailProposta] = useState(false);
  const [escludi, setEscludi] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let valida = true;
    dettaglioCassaforteRitiri(idMacchina)
      .then((x) => {
        if (!valida) return;
        const i = x.impostazioni;
        setD(x);
        setConflitto(false);
        setErrore(null);
        setCategoria(i.categoria_attivita ?? "");
        setGiorno(i.giorno_ritiro_fisso ? String(i.giorno_ritiro_fisso) : "");
        setConfermato(i.ritiro_fisso_confermato !== null);
        setOrario(i.orario_esercizio ?? "");
        // § 6: l'email di avviso si precompila da quella del punto vendita di AZNext.
        const proposta = i.email_avviso_cliente.length === 0 && !!i.email_punto_vendita_aznext;
        setEmail(proposta ? i.email_punto_vendita_aznext! : i.email_avviso_cliente.join(", "));
        setEmailProposta(proposta);
        setEscludi(i.escludi_dal_calcolo);
        setMotivo(i.motivo_esclusione ?? "");
        setNote(i.note ?? "");
      })
      .catch((err) => valida && setErrore(getErrorMessage(err, "Impossibile caricare le impostazioni.")));
    return () => {
      valida = false;
    };
  }, [idMacchina, caricamento]);

  async function salva(e: FormEvent) {
    e.preventDefault();
    setSalvo(true);
    setErrore(null);
    try {
      await aggiornaImpostazioni(idMacchina, {
        versione: d?.impostazioni.modificate?.il ?? null,
        categoria_attivita: categoria || null,
        // Il ritiro fisso si manda solo a chi lo può cambiare: per gli altri
        // resta com'è, senza farsi rifiutare il resto del form.
        ...(puoApprovare ? { giorno_ritiro_fisso: giorno ? Number(giorno) : null, ritiro_fisso_confermato: confermato && !!giorno } : {}),
        orario_esercizio: orario.trim() || null,
        email_avviso_cliente: email,
        escludi_dal_calcolo: escludi,
        motivo_esclusione: escludi ? motivo.trim() || null : null,
        note: note.trim() || null,
      });
      showToast("Impostazioni salvate.", "success");
      onSalvate();
    } catch (err) {
      setConflitto(axios.isAxiosError(err) && err.response?.status === 409);
      setErrore(getErrorMessage(err, "Impossibile salvare le impostazioni."));
    } finally {
      setSalvo(false);
    }
  }

  const proposto = d?.dati.ritiro_fisso_proposto ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={ID_TITOLO_IMPOSTAZIONI} tabIndex={-1} className="text-xl font-extrabold leading-tight text-ink outline-none">
            Impostazioni cassaforte
          </h2>
          <p className="mt-0.5 text-sm text-ritiri-th-testo">
            {d?.nome_punto ?? idMacchina} <span className="font-plex text-xs">{idMacchina}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onChiudi}
          className="inline-flex h-9 shrink-0 items-center rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm font-semibold text-ink hover:bg-ritiri-hover"
        >
          Chiudi
        </button>
      </div>

      {!d && !errore && <p className="text-sm text-ritiri-th-testo">Caricamento…</p>}
      {errore && !d && <p className="text-sm text-ink">{errore}</p>}

      {d && (
        <form onSubmit={salva} className="space-y-4 rounded-2xl border border-ritiri-bordo bg-white p-5" noValidate>
          <label className={etichetta}>
            Categoria di attività
            <select className={`${campo} h-10`} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">Nessuna (vale "Altro")</option>
              {categorie.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {d.impostazioni.categoria_origine === "script" && (
            <p className="-mt-2 text-xs text-ritiri-th-testo">Scelta dalla precompilazione: salvandone un'altra diventa manuale.</p>
          )}

          <fieldset className="space-y-2">
            <legend className={etichetta}>Ritiro fisso</legend>
            {proposto !== null && (
              <p className="text-xs text-ritiri-th-testo">I ritiri sembrano seguire un giorno fisso: ogni {nomeGiorno(proposto)}. È solo un suggerimento.</p>
            )}
            <select
              className={`${campo} h-10`}
              value={giorno}
              onChange={(e) => {
                setGiorno(e.target.value);
                if (!e.target.value) setConfermato(false);
              }}
              disabled={!puoApprovare}
              aria-label="Giorno del ritiro fisso"
            >
              <option value="">Nessuno</option>
              {[1, 2, 3, 4, 5].map((g) => (
                <option key={g} value={g}>
                  {nomeGiorno(g)}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={confermato} onChange={(e) => setConfermato(e.target.checked)} disabled={!puoApprovare || !giorno} />
              Confermato: è un ritiro fisso vero, non serve richiederlo quel giorno
            </label>
            {d.impostazioni.ritiro_fisso_confermato && (
              <p className="text-xs text-ritiri-th-testo">
                Confermato il {new Date(d.impostazioni.ritiro_fisso_confermato.il).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" })}
                {d.impostazioni.ritiro_fisso_confermato.da ? ` da ${d.impostazioni.ritiro_fisso_confermato.da}` : ""}.
              </p>
            )}
            {!puoApprovare && <p className="text-xs text-ritiri-th-testo">Il ritiro fisso lo imposta e lo conferma chi ha il permesso ritiri:approva.</p>}
          </fieldset>

          <label className={etichetta}>
            Orario di esercizio
            <textarea
              className={`${campo} min-h-[4.5rem] py-2`}
              value={orario}
              onChange={(e) => setOrario(e.target.value)}
              placeholder="Da lunedì a venerdì 08:30 – 12:00 14:00 – 17:30"
              maxLength={1000}
            />
          </label>

          <label className={etichetta}>
            Email avviso cliente (separate da virgola)
            <input type="text" className={`${campo} h-10`} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </label>
          {emailProposta && (
            <p className="-mt-2 text-xs text-ritiri-th-testo">Proposta dall'email del punto vendita su AZNext: si salva solo con "Salva".</p>
          )}

          <fieldset className="space-y-2">
            <legend className={etichetta}>Esclusione dal calcolo</legend>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={escludi} onChange={(e) => setEscludi(e.target.checked)} />
              Escludi questa cassaforte dal calcolo
            </label>
            {escludi && (
              <label className={etichetta}>
                Motivo (obbligatorio)
                <input type="text" className={`${campo} h-10`} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={1000} />
              </label>
            )}
          </fieldset>

          <label className={etichetta}>
            Note
            <textarea className={`${campo} min-h-[3.5rem] py-2`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </label>

          {errore && (
            <p className="text-sm text-danger" role="alert">
              {errore}
            </p>
          )}
          {conflitto && (
            <div className="flex flex-wrap items-center gap-3" data-impostazioni="conflitto">
              <p className="text-xs text-ritiri-th-testo">Ricaricando si vedono le impostazioni salvate dall'altro utente; le modifiche fatte qui vanno reinserite.</p>
              <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => setCaricamento((n) => n + 1)}>
                Ricarica
              </PulsanteRitiri>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <PulsanteRitiri type="submit" inCorso={salvo} disabled={conflitto}>
              Salva
            </PulsanteRitiri>
            <PulsanteRitiri type="button" variante="secondario" onClick={onChiudi}>
              Annulla
            </PulsanteRitiri>
          </div>
          {d.impostazioni.modificate && (
            <p className="text-xs text-ritiri-th-testo">
              Ultima modifica {new Date(d.impostazioni.modificate.il).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}
              {d.impostazioni.modificate.da ? ` da ${d.impostazioni.modificate.da}` : ""}.
            </p>
          )}
        </form>
      )}
    </div>
  );
}
